import { record } from 'fp-ts';
import {
    FieldErrors,
    FieldValues,
    FormProvider,
    FormState,
    SubmitHandler as FormSubmitHandler,
    ResolverError,
    ResolverOptions,
    ResolverSuccess,
    SubmitErrorHandler,
    useForm,
    UseFormProps,
    UseFormReset,
} from 'react-hook-form';
import { FormCustomContext } from './context';

type ResolverResult<InputValues extends FieldValues = FieldValues, OutputValues extends FieldValues = InputValues> =
    | ResolverSuccess<OutputValues>
    | ResolverError<InputValues>;
type Resolver<
    InputValues extends FieldValues = FieldValues,
    OutputValues extends FieldValues = InputValues,
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    Context = any,
> = (
    values: InputValues,
    context: Context | undefined,
    options: ResolverOptions<InputValues>
) => Promise<ResolverResult<InputValues, OutputValues>> | ResolverResult<InputValues, OutputValues>;

export type SubmitHandler<InputValues extends object, OutputValues extends object = InputValues> = (
    values: OutputValues,
    extra: {
        dirtyFields: FormState<InputValues>['dirtyFields'];
        reset: UseFormReset<InputValues>;
    }
) => Promise<unknown> | void;

type SubmitFailHandler<V extends object> = (errors: FieldErrors<V>) => void;

export interface UseFormControlsParams<InputValues extends object, OutputValues extends object = InputValues>
    extends Omit<UseFormProps<InputValues>, 'resolver'> {
    id?: string;
    dataTestForm?: string;
    onSubmit: SubmitHandler<InputValues, OutputValues>;
    onSubmitFail?: SubmitFailHandler<InputValues>;
    resolver?: Resolver<InputValues, OutputValues>;
    readOnly?: boolean;
}

/**
 * @description This hook should be used in case of submit, reset or any other action should be used above the form
 * declaration e.g. Dialog component actions. In other cases please use `Form` component.
 */
export const useFormControls = <InputValues extends object, OutputValues extends object = InputValues>({
    id,
    dataTestForm,
    onSubmitFail,
    onSubmit,
    readOnly = false,
    ...formProps
}: UseFormControlsParams<InputValues, OutputValues>) => {
    const formContext = useForm<InputValues, FormCustomContext>({ ...formProps, context: { readOnly } });

    const {
        formState: { dirtyFields, isValid, isSubmitting, errors },
        reset,
        setError,
        trigger: validate,
        watch,
        register,
        setValue,
        resetField,
        control,
    } = formContext;

    /**
     * `dirtyFields` parameter check is more accurate than `isDirty` as `isDirty` will be updated on values set in edit form.
     */
    const hasDirtyFields = !record.isEmpty(dirtyFields as Record<string, unknown>);

    const handleSubmit: FormSubmitHandler<OutputValues> = async (values) => onSubmit(values, { dirtyFields, reset });

    const handleInvalidSubmit: SubmitErrorHandler<InputValues> = (submitErrors) => {
        if (onSubmitFail) {
            onSubmitFail(submitErrors);
        }
    };

    // @ts-expect-error Values are not compatible to OutputValues, form doesn't support input/output values type from resolver
    const submit = formContext.handleSubmit(handleSubmit, handleInvalidSubmit);

    const renderFormContent = (children: React.ReactNode) => (
        <form id={id} onSubmit={submit} noValidate data-test-form={dataTestForm}>
            <FormProvider {...formContext}>{children}</FormProvider>
        </form>
    );

    return {
        hasDirtyFields,
        dirtyFields,
        control,
        isValid,
        isSubmitting,
        renderFormContent,
        reset,
        resetField,
        setError,
        submit,
        validate,
        watch,
        register,
        setValue,
        errors,
    };
};
